'use client';
import { IBranch } from '@/_types';
import { getBrandSlug } from '@/_lib/brand-href';
import { formatPhoneNumber } from '@/_utils/formatPhoneNumber';
import { Button } from '@/components/ui/button';
import {
  Center,
  Flex,
  Grid,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { useRouter } from '@/i18n/navigation';
import React, { Dispatch } from 'react';
import { ISavedBranch } from '@/_store/types';

interface IProps {
  data: IBranch[];
  branchRefs?: any;
  selectedBranch: ISavedBranch | null;
  setSelectedBranch: Dispatch<IBranch | null>;
  onSelectBranch?: (val: IBranch | null) => void;
  activeShipperId?: string;
}

const BranchItem = ({
  data,
  branchRefs,
  onSelectBranch,
  selectedBranch,
  setSelectedBranch,
  activeShipperId,
}: IProps) => {
  const t = useTranslations();
  const router = useRouter();

  const redirectToDetailed = (e: any, slug: string) => {
    e.stopPropagation();
    const shipperSlug = getBrandSlug(activeShipperId);
    const url = shipperSlug
      ? `/branches/${slug}?shipper_id=${shipperSlug}`
      : `/branches/${slug}`;
    router.push(url);
  };

  return (
    <>
      <Stack gap={3} w={'full'}>
        {data?.map((el) => (
          <VStack
            key={el.id}
            ref={(elRef) => {
              if (elRef) {
                if (branchRefs?.current) {
                  branchRefs.current[el.id] = elRef;
                }
              }
            }}
            alignItems={'start'}
            border={'1px solid'}
            bgColor={'#F9F8F7'}
            borderColor={
              selectedBranch?.id === el.id ? 'primary' : 'background'
            }
            rounded={16}
            cursor={'pointer'}
            _hover={{
              border: '1px solid',
              borderColor: 'primary',
            }}
            gap={2}
            p={4}
            mb={2}
            onClick={() => {
              setSelectedBranch(el);
              onSelectBranch?.(el);
            }}
          >
            <Stack w={'full'} gap={1}>
              <HStack justifyContent={'space-between'} w={'full'}>
                <HStack alignItems={'center'} gap={2}>
                  <Text fontWeight={700} fontSize={18}>
                    {el?.name}
                  </Text>
                </HStack>
                <HStack gap={2}>
                  {el?.has_parking && (
                    <Center
                      px={2}
                      py={1}
                      bg='#4E60FF33'
                      color='#4E60FF'
                      borderRadius='10px'
                      fontSize='12px'
                      fontWeight={500}
                    >
                      Парковка
                    </Center>
                  )}
                </HStack>
              </HStack>

              <Text fontSize='14px' color={'gray.800'}>
                {el.address}
              </Text>
            </Stack>
            <Flex>
              <Text fontSize={15} color='gray.800' fontWeight={500}>
                {t('schedule')}:
              </Text>
              <Text fontSize={14} ml='4px' color='text'>
                {el?.open_time}-{el?.close_time}
              </Text>
            </Flex>

            <Flex>
              <Text fontSize={15} color='gray.800' fontWeight={500}>
                {t('phone')}:
              </Text>
              <Text fontSize={14} ml='4px' color='text'>
                <Link href={el?.phone ? `tel:${el?.phone}` : ''}>
                  {formatPhoneNumber(el?.phone)}
                </Link>
              </Text>
            </Flex>
            <Grid
              w='full'
              rowGap={2}
              columnGap={2}
              fontSize='14px'
              templateColumns='1fr 1fr'
            >
              <Link
                href={`https://yandex.com/maps/?ll=${el?.location?.long}%2C${el?.location?.lat}&mode=whatshere&whatshere%5Bpoint%5D=${el?.location?.long}%2C${el?.location?.lat}&z=14`}
                target='_blank'
                rel='noreferrer'
                style={{ flex: 1 }}
              >
                <Center
                  bg='primary'
                  color='primary.text'
                  _hover={{
                    bg: 'primary.hover',
                  }}
                  px={4}
                  h='40px'
                  fontSize='14px'
                  borderRadius='8px'
                  fontWeight={700}
                  textAlign='center'
                >
                  {t('build_route')}
                </Center>
              </Link>

              <Button
                flex={1}
                size={'md'}
                rounded={8}
                fontSize='14px'
                fontWeight={600}
                color={'primary'}
                variant={'outline'}
                colorScheme={'primary'}
                borderColor={'primary'}
                onClick={(e) => redirectToDetailed(e, el?.slug)}
              >
                {t('read_more')}
              </Button>
            </Grid>
          </VStack>
        ))}
      </Stack>
    </>
  );
};

export default BranchItem;
